import {
  Avatar,
  Box,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LinkReturnToMain } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { useActivitiesLobby } from 'Hooks/useActivitiesLobby';
import { PurpleSearch, PurpleSearchIcon, PurpleX } from 'Images';
import { useEffect, useRef, useState } from 'react';
import { ResultItem } from './ResultItem';
import { sendEvent } from 'Utils/sendEvent';

export interface IResult {
  label: string;
  onClick?: () => void;
  isDownload?: boolean;
}

interface IActivitiesSearch {
  placeholder?: string;
  source?: string;
}

export const ActivitiesSearchBar = ({
  placeholder,
  source,
}: IActivitiesSearch) => {
  const [isActive, setIsActive] = useState(false);
  const [value, setValue] = useState('');
  const [results, setResults] = useState<IResult[]>([]);

  const { activitiesList } = useActivitiesLobby();

  const theme = useTheme();
  const isScreenXl = useMediaQuery(theme.breakpoints.up('xl'));

  const popularSuggestions = [
    {
      label: activitiesList[2].activities[0].label,
      onClick: activitiesList[2].activities[0].onClick,
    },
    {
      label: activitiesList[5].activities[0].label,
      onClick: activitiesList[5].activities[0].onClick,
    },
    {
      label: activitiesList[2].activities[2].label,
      onClick: activitiesList[2].activities[2].onClick,
    },
  ];

  const dictionary = useDictionary(Dictionary, DICTIONARY.ActivitiesLobby);
  const isDesktop = useIsDesktop();

  const inputRef = useRef();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (isActive && inputRef.current) (inputRef.current as HTMLElement).focus();
  }, [isActive]);

  useEffect(() => {
    const newResults: IResult[] = [];
    activitiesList.forEach((activity) => {
      activity.activities.forEach((item) => {
        [...(item.searchWords || []), item.label].every((word) => {
          let isFound: boolean = word
            .toLowerCase()
            .includes(value.toLocaleLowerCase());
          if (isFound) {
            newResults.push({
              label: item.label,
              onClick: item.onClick,
              isDownload: item.showIcon,
            });
          }
          return !isFound;
        });
      });
    });

    setResults(newResults);
  }, [value]);

  return (
    <>
      {!isActive && (
        <Stack
          component={'button'}
          sx={{
            width: isDesktop ? '564px' : '100%',
            height: '56px',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: isDesktop ? 'flex-start' : 'space-between',
            bgcolor: 'white',
            border: '1px solid',
            borderColor: 'primary.shade_200',
            borderRadius: '30px',
            mt: '24px',
            cursor: 'text',
            p: isDesktop ? undefined : '6px 6px 6px 16px',
            gap: isDesktop ? '10px' : undefined,
            '&:focus': { outlineColor: 'rgb(21, 82, 232)' },
          }}
          onClick={() => {
            if (source === 'home') {
              sendEvent('Search Page', 'HomePageSearch');
            } else if (source === 'activities') {
              sendEvent('Activities Page', 'ActionsLobbySearch');
            }
            setIsActive(true);
          }}
        >
          {isDesktop ? (
            <>
              <Avatar src={PurpleSearch} alt='' />
              <Typography variant={isDesktop ? 'body3' : 'body5'}>
                {placeholder || dictionary.searchButton}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant={'body3'}>
                {placeholder || dictionary.searchButton}
              </Typography>
              <Avatar src={PurpleSearchIcon} alt='' />
            </>
          )}
        </Stack>
      )}

      <Modal
        open={isActive}
        sx={{
          position: isDesktop ? 'absolute' : 'fixed',
          bgcolor: isDesktop ? undefined : 'primary.shade_50',
          right: 0,
          top: isDesktop ? 0 : '72px',
          height: isDesktop ? undefined : 'calc(100vh - 72px)',
          opacity: isActive ? 1 : 0,
          zIndex: isActive ? 99999 : -99999,
          overflow: isDesktop ? undefined : 'hidden',
          direction: 'ltr',
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setIsActive(false);
        }}
      >
        <Stack
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: isDesktop && isScreenXl ? 'calc(100vw - 306px)' : '100vw',
            p: isDesktop ? '32px 64px 48px' : '20px 24px 88px',
            bgcolor: 'primary.shade_50',
            zIndex: isActive ? 99999 : -99999,
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            overflow: isDesktop ? 'hidden' : 'auto',
            opacity: isActive ? 1 : 0,
            transition: isDesktop ? 'opacity .5s' : undefined,
            maxHeight: isDesktop ? undefined : 'calc(100vh - 72px)',
          }}
        >
          {isActive && (
            <LinkReturnToMain
              label={dictionary.back}
              onClick={() => setIsActive(false)}
              tabIndex={0}
            />
          )}
          <Stack
            sx={{
              width: isDesktop ? '564px' : undefined,
              height: '56px',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: isDesktop ? 'space-between' : undefined,
              bgcolor: 'white',
              border: '1px solid',
              borderColor: 'primary.shade_200',
              borderRadius: '30px',
              mt: '24px',
              cursor: 'text',
            }}
            onClick={() => {
              setIsActive(true);
            }}
          >
            <Avatar sx={{ ml: '4px' }} src={PurpleSearch} alt='' />
            {isActive && (
              <fieldset>
                <legend>{dictionary.searchButton}</legend>
                <TextField
                  inputRef={inputRef}
                  sx={{
                    width: isDesktop ? '450px' : undefined,
                    ml: isDesktop ? undefined : '5px',
                  }}
                  inputProps={{
                    style: {
                      padding: 0,
                    },
                    'aria-label': placeholder || dictionary.searchButton,
                  }}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              </fieldset>
            )}
            <IconButton
              sx={{ mr: '12px', ml: isDesktop ? undefined : 'auto' }}
              onClick={() => setValue('')}
              tabIndex={isActive ? undefined : -1}
            >
              <Avatar
                sx={{ width: '20px', height: '20px', cursor: 'pointer' }}
                src={PurpleX}
                alt={Dictionary[DICTIONARY.General].clearSearch}
                aria-label={Dictionary[DICTIONARY.General].clearSearch}
              />
            </IconButton>
          </Stack>
          {value.length >= 3 && results.length === 0 && (
            <>
              <Typography variant='h3' mt='48px'>
                {dictionary.noResults.title}
              </Typography>
              <Typography variant='body3' mt='8px'>
                {dictionary.noResults.subtitle}
              </Typography>
            </>
          )}
          <Typography component={'h2'} variant='h3' mt='48px'>
            {value.length < 3 || results.length === 0
              ? dictionary.popularSuggestions
              : dictionary.whichActivity}
          </Typography>
          {isActive && (
            <Stack mt='24px' gap='16px'>
              {value.length < 3 || results.length === 0
                ? popularSuggestions.map((item, index: number) => (
                    <ResultItem key={index} item={item} />
                  ))
                : results.map((item, index: number) => (
                    <ResultItem key={index} item={item} />
                  ))}
            </Stack>
          )}
        </Stack>
      </Modal>
      <Box
        sx={{
          position: 'fixed',
          width: '100vw',
          height: '100vh',
          top: '80px',
          right: 0,
          bgcolor: 'secondary.dark',
          opacity: isActive ? 0.8 : 0,
          zIndex: isActive ? 9999 : -9999,
          transition: isDesktop
            ? isActive
              ? 'opacity .5s'
              : undefined
            : undefined,
        }}
        onClick={() => setIsActive(false)}
      />
    </>
  );
};
